"use client";

// useEditStack — a tiny in-memory optimistic undo/redo stack for the shell's
// manual edits. Started as rename-only; now an op UNION — renames, item/project
// moves, and park/unpark — so ⌘Z/⇧⌘Z and the options-menu undo/redo buttons can
// always walk back. Ephemeral per session (matches ⌘Z expectations; not
// persisted cross-device). The hook stays dumb: it records ops and replays them
// through the caller's `apply(op, direction)` dispatcher, so the server stays
// the source of truth. (Items with a stable key — nodes/artifacts/tasks/
// projects — round-trip cleanly; a dimension's key derives from its name, so
// its undo is best-effort.)

import { useCallback, useRef, useState } from "react";

export type EditOp =
  /** an inline rename — undo re-applies `before`, redo `after`. */
  | { kind: "rename"; key: string; projectId: string; before: string; after: string }
  /** a moveInto/paste that succeeded — undo re-issues the move back to where it
   *  came from (POST /api/os/paste, or /api/os/move for a bare project key). */
  | { kind: "move"; sourceKey: string; name: string; fromProjectId: string; toProjectId: string; fromParentPath: string; toParentPath: string }
  /** a park/unpark toggle — undo/redo re-toggles POST /api/os/appearance { park }. */
  | { kind: "park"; projectId: string; name: string; parked: boolean };

export function useEditStack(apply: (op: EditOp, direction: "undo" | "redo") => void) {
  const undoStack = useRef<EditOp[]>([]);
  const redoStack = useRef<EditOp[]>([]);
  // both depths force a render on change, so callers can show each side's
  // enabled state (the undo/redo buttons grey out when a stack side is empty).
  const [depths, setDepths] = useState({ undo: 0, redo: 0 });

  const push = useCallback((op: EditOp) => {
    if (op.kind === "rename" && op.before === op.after) return;
    undoStack.current.push(op);
    redoStack.current = [];
    setDepths({ undo: undoStack.current.length, redo: 0 });
  }, []);

  const undo = useCallback((): EditOp | null => {
    const op = undoStack.current.pop();
    if (!op) return null;
    redoStack.current.push(op);
    setDepths({ undo: undoStack.current.length, redo: redoStack.current.length });
    apply(op, "undo");
    return op;
  }, [apply]);

  const redo = useCallback((): EditOp | null => {
    const op = redoStack.current.pop();
    if (!op) return null;
    undoStack.current.push(op);
    setDepths({ undo: undoStack.current.length, redo: redoStack.current.length });
    apply(op, "redo");
    return op;
  }, [apply]);

  return { push, undo, redo, canUndo: depths.undo > 0, canRedo: depths.redo > 0 };
}
